import { Attachment, ProductType } from '@/data/types/Product';
import { Slide } from '@/data/types/Slide';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';

/**
 * Processes product attachments and creates a sorted array of slides
 * for a product gallery or carousel.
 */
export const getProductImageSlides = (
	product: ProductType,
	attachments: Attachment[],
	skuImage: string
): Slide[] => {
	// 1. Find the special '-m.jpg' image for the first slide's thumbnail.
	const mainThumbnailAttachment = attachments?.find((attachment) =>
		attachment.identifier?.match(/^(.*)-m\.(jpe?g|png|gif|webp)$/)
	);

	// 2. Group the rest of the images that follow the 'a#/p#' naming convention.
	const restOfSlidesMap = attachments
		?.filter((attachment) => attachment.usage === 'IMAGES')
		?.reduce((acc, attachment) => {
			// New regex to capture the base name, type ('a' or 'p'), and number.
			const match = attachment.identifier?.match(/^(.*)-([ap])(\d+)\.(jpe?g|png|gif|webp)$/i);

			if (!match) {
				return acc; // Ignore attachments that don't match the new pattern.
			}

			const baseIdentifier = match[1];
			const imageType = match[2];
			const imageNumber = parseInt(match[3], 10);
			const imageURL = CDN_BASE_URL + '/' + attachment.identifier;

			const groupKey = `${baseIdentifier}-${imageNumber}`;

			// Create or update the slide object for this number group.
			acc[groupKey] = acc[groupKey] || {
				name: attachment.name || '',
				fullImage: '',
				fullImageRaw: '',
				thumbnail: '',
				thumbnailRaw: '',
				// Store the sort order for later
				sortOrder: imageNumber,
			};

			if (imageType === 'a') {
				acc[groupKey].fullImage = imageURL;
				acc[groupKey].fullImageRaw = imageURL;
			} else if (imageType === 'p') {
				acc[groupKey].thumbnail = imageURL;
				acc[groupKey].thumbnailRaw = imageURL;
			}

			return acc;
		}, {} as Record<string, Partial<Slide> & { sortOrder: number }>);

	// 3. Convert the map into a sorted array of Slide objects.
	const restOfSlides = Object.values(restOfSlidesMap ?? {})
		.sort((a, b) => a.sortOrder - b.sortOrder)
		.map((slide) => {
			const fullImage = slide.fullImage || skuImage;
			const fullImageRaw = slide.fullImageRaw || skuImage;
			const { sortOrder, ...rest } = slide;
			return {
				...rest,
				fullImage,
				fullImageRaw,
			};
		}) as Slide[];

	// 4. Create the first slide with the specific logic.
	const firstSlide: Slide = {
		name: mainThumbnailAttachment?.name || product?.name || '',
		fullImage: skuImage,
		fullImageRaw: skuImage,
		thumbnail: mainThumbnailAttachment
			? CDN_BASE_URL + '/' + mainThumbnailAttachment.identifier
			: '',
		thumbnailRaw: mainThumbnailAttachment
			? CDN_BASE_URL + '/' + mainThumbnailAttachment.identifier
			: '',
	};

	// 5. Combine the first slide with the rest of the sorted slides.
	return [firstSlide, ...restOfSlides];
};
